/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { axiosInstance } from '@coze-arch/bot-http';

import { getSessionAuthHeaders } from './auth-headers';

interface CozeBffBody<T> {
  code: number;
  msg?: string;
  data: T;
}

function isCozeBffBody<T>(value: unknown): value is CozeBffBody<T> {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'code' in value &&
    'data' in value
  );
}

function buildBffRequestConfig(path: string) {
  const auth = getSessionAuthHeaders(path);
  return Object.keys(auth).length > 0 ? { headers: auth } : {};
}

/** bot-api 拦截器可能已解包为 body，兼容 raw AxiosResponse */
export function unwrapCozeBffPayload<T>(res: unknown): T {
  if (isCozeBffBody<T>(res)) {
    return res.data;
  }
  if (
    res !== null &&
    res !== undefined &&
    typeof res === 'object' &&
    'data' in res
  ) {
    const nested = (res as { data: unknown }).data;
    if (isCozeBffBody<T>(nested)) {
      return nested.data;
    }
  }
  return res as T;
}

/** 登录/桥接等返回完整 passport body（含 data、uc_tenant_id 等顶层字段） */
export function unwrapCozePassportBody<T>(res: unknown): T {
  if (
    res !== null &&
    res !== undefined &&
    typeof res === 'object' &&
    'code' in res
  ) {
    return res as T;
  }
  if (
    res !== null &&
    res !== undefined &&
    typeof res === 'object' &&
    'data' in res
  ) {
    const nested = (res as { data: unknown }).data;
    if (
      nested !== null &&
      nested !== undefined &&
      typeof nested === 'object' &&
      'code' in nested
    ) {
      return nested as T;
    }
  }
  return res as T;
}

/** GET BFF：返回 body.data */
export async function cozeUcBffGet<T>(
  path: string,
  params?: Record<string, string>,
): Promise<T> {
  const res = await axiosInstance.get(path, {
    params,
    ...buildBffRequestConfig(path),
  });
  return unwrapCozeBffPayload<T>(res);
}

/** POST BFF：返回完整 passport body */
export async function cozeUcBffPost<T>(
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await axiosInstance.post(
    path,
    body ?? {},
    buildBffRequestConfig(path),
  );
  return unwrapCozePassportBody<T>(res);
}
